import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isEmailVerified } from '../../../utils/firebase.utils';
import { emailService } from '../../../services/emailService';

const EmailVerificationSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'checking' | 'success' | 'error'>('checking');

  useEffect(() => {
    const checkVerification = async () => {
      try {
        // Check if user is verified
        if (user && isEmailVerified()) {
          setVerificationStatus('success');
          
          // Note: Welcome email is now sent immediately upon signup
          // No need to trigger it again after verification
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setVerificationStatus('error');
        }
      } catch (error) {
        console.error('Verification check failed:', error);
        setVerificationStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    // Small delay to ensure Firebase has processed the verification
    setTimeout(checkVerification, 1000);
  }, [user, navigate]);

  const handleContinue = () => {
    navigate('/');
  };

  if (isLoading || verificationStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter">
              Verifying Your Email
            </h1>
            
            <p className="text-gray-600 mb-6">
              Please wait while we confirm your email verification...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-3xl">
                <i className="fas fa-check" />
              </span>
            </div>
            
            <h1 className="text-2xl font-bold mb-4 font-inter text-green-600">
              Email Verified Successfully!
            </h1>
            
            <p className="text-gray-600 mb-6">
              Welcome to Apply.AI! Your email has been verified and your account is now fully activated.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                🎉 You now have full access to all Apply.AI features including AI essay analysis, college recommendations, and deadline tracking.
              </p>
            </div>
            
            <button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-base rounded-lg h-12 flex items-center justify-center transition-all mb-4"
            >
              Continue to Dashboard
            </button>
            
            <p className="text-sm text-gray-500">
              Redirecting automatically in a few seconds...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-100 to-blue-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-exclamation-triangle" />
            </span>
          </div>
          
          <h1 className="text-2xl font-bold mb-4 font-inter text-red-600">
            Verification Failed
          </h1>
          
          <p className="text-gray-600 mb-6">
            We couldn't verify your email address. This might be because the verification link has expired or is invalid.
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              Please try requesting a new verification email from your account settings, or contact support if the problem persists.
            </p>
          </div>
          
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold text-base rounded-lg h-12 flex items-center justify-center transition-all"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationSuccessPage;